import {isValidItemDecimal, MAX_ITEM_NUMBER_ABS} from '@/utils/itemInputValidation';

export const MAX_TIME_DURATION_HOURS = 99;
export const MAX_TIME_TOTAL_MINUTES = 2147483647;

const SERVER_DECIMAL_SCALE = 5;
const MAX_SCALED_TOTAL = MAX_ITEM_NUMBER_ABS + '00000';

export function isValidTimeRate(value) {
    return isValidItemDecimal(value, false, SERVER_DECIMAL_SCALE);
}

export function validateTimeRecord(time, companyUUID) {
    if (time == null || Array.isArray(time)) {
        throw new Error('Time response is invalid');
    }
    if (time.innerUUID.length === 0 || time.companyUUID !== companyUUID || time.syncStatus === 1) {
        throw new Error('Time response scope is invalid');
    }
    if ((time.showRateName !== 0 && time.showRateName !== 1)
        || (time.billed !== 0 && time.billed !== 1)) {
        throw new Error('Time response flags are invalid');
    }
    if (!Number.isInteger(time.totalMinutes) || time.totalMinutes < 0
        || time.totalMinutes > MAX_TIME_TOTAL_MINUTES) {
        throw new Error('Time response duration is invalid');
    }
    if (!isValidTimeRate(time.rate) || !isValidTimeRate(time.total)) {
        throw new Error('Time response amounts are invalid');
    }
}

function stripLeadingZeros(value) {
    const stripped = value.replace(/^0+/, '');
    return stripped === '' ? '0' : stripped;
}

function multiplyIntegerString(value, multiplier) {
    if (value === '0' || multiplier === 0) {
        return '0';
    }
    let carry = 0;
    let result = '';
    for (let index = value.length - 1; index >= 0; index--) {
        const product = Number(value.charAt(index)) * multiplier + carry;
        result = String(product % 10) + result;
        carry = Math.floor(product / 10);
    }
    return String(carry === 0 ? '' : carry) + result;
}

function divideIntegerString(value, divisor) {
    let remainder = 0;
    let quotient = '';
    for (let index = 0; index < value.length; index++) {
        const dividend = remainder * 10 + Number(value.charAt(index));
        const digit = Math.floor(dividend / divisor);
        if (quotient !== '' || digit !== 0) {
            quotient += String(digit);
        }
        remainder = dividend % divisor;
    }
    return quotient === '' ? '0' : quotient;
}

function minimumIntegerString(left, right) {
    const normalizedLeft = stripLeadingZeros(left);
    const normalizedRight = stripLeadingZeros(right);
    if (normalizedLeft.length !== normalizedRight.length) {
        return normalizedLeft.length < normalizedRight.length ? normalizedLeft : normalizedRight;
    }
    return normalizedLeft <= normalizedRight ? normalizedLeft : normalizedRight;
}

function formatScaledTotal(scaledTotal) {
    const padded = scaledTotal.padStart(SERVER_DECIMAL_SCALE + 1, '0');
    const integerPart = padded.substring(0, padded.length - SERVER_DECIMAL_SCALE);
    const fractionPart = padded.substring(padded.length - SERVER_DECIMAL_SCALE).replace(/0+$/, '');
    return fractionPart === '' ? integerPart : integerPart + '.' + fractionPart;
}

export function calculateTimeTotal(rate, totalMinutes) {
    if (!isValidTimeRate(rate)) {
        throw new Error('Time rate is invalid');
    }
    if (!Number.isInteger(totalMinutes) || totalMinutes < 0 || totalMinutes > MAX_TIME_TOTAL_MINUTES) {
        throw new Error('Time duration is invalid');
    }

    const parts = String(rate).trim().split('.');
    const fraction = parts.length === 2 ? parts[1] : '';
    const unscaledRate = stripLeadingZeros(parts[0] + fraction);
    const multiplied = multiplyIntegerString(unscaledRate, totalMinutes);
    const divisor = 60 * Math.pow(10, fraction.length);
    const scaledTotal = divideIntegerString(multiplied + '00000', divisor);
    return formatScaledTotal(minimumIntegerString(scaledTotal, MAX_SCALED_TOTAL));
}
